import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, Plus, Settings, Star } from 'lucide-react';
import { Icon } from '@/core/ui/icons';
import type { Stage, TaskListItem } from '@cg-projects/shared';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useTasksStore } from '@/features/tasks/store/tasksStore';
import { prefetchTask, useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { useFilterStore } from '@/features/filters/store/filterStore';
import { useLookupStore } from '@/features/lookups/store/lookupStore';
import { filterTasks } from '@/features/tasks/filter';
import { groupTasks, sortTasks, dueBucket, createDefaultsFor, type DateField, type GroupBy, type GroupCreateDefaults } from '@/features/tasks/grouping';
import { moveStage, patchTask, createTask, completeWithUndo } from '@/features/tasks/mutations';
import { AssigneeStack, TagChip, KanbanDot, StateBadge, Skeleton, LoadError } from '@/core/ui/atoms';
import { formatDate, relDueLabel } from '@/core/ui/helpers';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useToastStore } from '@/core/ui/toastStore';
import { useUiStore } from '@/core/ui/uiStore';
import { api } from '@/core/api/apiClient';
import { StageManagerModal } from './StageManagerModal';
import { TaskContextMenu } from '@/features/tasks/components/TaskContextMenu';

const VIRTUALIZE_THRESHOLD = 40;
const CLOSED = new Set(['1_done', '1_canceled']);
// A state chip is only worth showing when it flags attention. "In Progress"/"Done" are the
// default — stamping every card with them is pure noise, so those stay unlabelled.
const NOTABLE_STATE = new Set(['02_changes_requested', '03_approved', '04_waiting_normal', '1_canceled']);
// Terminal / reference stages — collapse by default so active work dominates the board.
const TERMINAL_STAGE = /\b(archive|archived|done|complete|completed|closed|cancel|cancelled|canceled|old)\b/i;

// Hover-intent prefetch (instant-open layer 3): 100ms of dwell on a card warms
// the task-detail cache so the click that follows renders with no spinner.
// One timer is enough — the pointer only hovers one card at a time. Touch-only
// devices never produce a useful mouseenter, so they skip the wiring entirely.
// typeof-guarded: the client unit tests import under NODE, where module-scope
// window access crashes the require before a single test runs.
const HOVERABLE = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
let hoverTimer: ReturnType<typeof setTimeout> | undefined;
const hoverStart = (id: number) => {
  if (!HOVERABLE) return;
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(() => prefetchTask(id), 100);
};
const hoverEnd = () => clearTimeout(hoverTimer);

function bucketColor(b: ReturnType<typeof dueBucket>): string {
  return b === 'overdue' ? 'text-danger' : b === 'today' || b === 'week' ? 'text-warn' : 'text-muted';
}

function CardContent({ task, hideDates = false }: { task: TaskListItem; dateField?: DateField; hideDates?: boolean }) {
  const tags = task.contentTags.length ? task.contentTags : task.tags;
  // Card anatomy is FIXED: title row → (chips) → (meta row). Rows that have nothing
  // to say don't render, so a bare task is a compact one-liner instead of a padded
  // empty box — a 38-card column must read as a list, not a wall.
  const shown = tags.slice(0, 3);
  const extra = tags.length - shown.length;
  // `hideDates` = this board has declared both dates past (Area.hideKeyDates —
  // Content Calendar: already shot, already posted). The chips don't draw, and the
  // meta row must not count them either, or a card whose ONLY meta was a date
  // renders an empty strip under its title.
  const showDates = !hideDates;
  const hasMetaLeft = task.kanbanState === 'blocked' || task.blockedByCount > 0
    || (showDates && (Boolean(task.filmDate) || Boolean(task.deadline))) || task.childCount > 0;
  const hasMetaRight = NOTABLE_STATE.has(task.state) || task.assignees.length > 0;
  const subPct = task.childCount > 0 ? Math.round((task.childDoneCount / task.childCount) * 100) : 0;
  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="text-[13px] font-medium leading-snug text-ink">{task.name}</span>
        {task.priority === '1' && <span className="shrink-0 text-warn" title="High priority"><Icon i={Star} size={14} className="fill-current" /></span>}
      </div>
      {shown.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {shown.map((t) => <TagChip key={`${t.id}-${t.name}`} tag={t} />)}
          {extra > 0 && (
            <span className="rounded bg-hover px-1.5 py-0.5 text-[10px] font-medium text-faint" title={tags.slice(3).map((t) => t.name).join(' · ')}>
              +{extra}
            </span>
          )}
        </div>
      )}
      {(hasMetaLeft || hasMetaRight) && (
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
            {task.kanbanState === 'blocked' && <KanbanDot state="blocked" />}
            {task.blockedByCount > 0 && <span className="font-medium text-danger" title={`Blocked by ${task.blockedByCount} task(s)`}>{task.blockedByCount} blocked</span>}
            {/* Prefixes only when BOTH dates exist — one date needs no disambiguating. */}
            {showDates && task.filmDate && <span className={bucketColor(dueBucket(task.filmDate))} title={`Key date: ${formatDate(task.filmDate)}`}>{task.deadline ? `Film ${relDueLabel(task.filmDate)}` : relDueLabel(task.filmDate)}</span>}
            {showDates && task.deadline && <span className={bucketColor(dueBucket(task.deadline))} title={`Deadline: ${formatDate(task.deadline)}`}>{task.filmDate ? `Due ${relDueLabel(task.deadline)}` : relDueLabel(task.deadline)}</span>}
            {(task.checkTotal ?? 0) > 0 && (
              // Checklist rollup ("as boxes get ticked, the board card quietly
              // shows 4/6") — a checkbox glyph distinguishes it from the
              // sub-task fraction beside it.
              <span className="flex items-center gap-1 tabular-nums" title={`${task.checkDone ?? 0}/${task.checkTotal} checklist items ticked`}>
                <span className={(task.checkDone ?? 0) >= (task.checkTotal ?? 0) ? 'text-success' : 'text-faint'}>☑ {task.checkDone ?? 0}/{task.checkTotal}</span>
              </span>
            )}
            {task.childCount > 0 && (
              <span className="flex items-center gap-1" title={`${task.childDoneCount}/${task.childCount} sub-tasks done`}>
                <span className="h-[3px] w-7 overflow-hidden rounded-full bg-hover">
                  <span
                    className="block h-full rounded-full"
                    style={{ width: `${subPct}%`, background: subPct >= 100 ? 'var(--color-success)' : 'var(--color-brand)' }}
                  />
                </span>
                <span className={`tabular-nums ${subPct >= 100 ? 'text-success' : 'text-faint'}`}>{task.childDoneCount}/{task.childCount}</span>
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {NOTABLE_STATE.has(task.state) && <StateBadge state={task.state} />}
            <AssigneeStack assignees={task.assignees} />
          </div>
        </div>
      )}
    </>
  );
}

function DraggableCard({ task, dateField, hideDates, orderedIds, onContext }: {
  task: TaskListItem;
  dateField: DateField;
  /** This board draws no shoot/delivery date on its cards (Area.hideKeyDates). */
  hideDates: boolean;
  /** The column's VISIBLE task ids in render order — the range shift-click selects over. */
  orderedIds: number[];
  onContext: (task: TaskListItem, x: number, y: number) => void;
}) {
  const open = useTaskDetailStore((s) => s.open);
  const isSelected = useTasksStore((s) => s.selectedIds.includes(task.id));
  const toggleSelected = useTasksStore((s) => s.toggleSelected);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task.id, data: { task } });
  const done = CLOSED.has(task.state);

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        // Shift-click = range select from the last selection anchor over this
        // column's visible order (§5E; tasksStore.selectRange contract). No
        // anchor yet → a plain toggle starts one.
        if (e.shiftKey) {
          const st = useTasksStore.getState();
          if (st.lastSelectedId !== null) st.selectRange(st.lastSelectedId, task.id, orderedIds);
          else st.toggleSelected(task.id);
          return;
        }
        // Ctrl/Cmd-click = add to the bulk selection instead of opening.
        if (e.ctrlKey || e.metaKey) { toggleSelected(task.id); return; }
        void open(task.id);
      }}
      onContextMenu={(e) => { e.preventDefault(); onContext(task, e.clientX, e.clientY); }}
      // dnd-kit already announces this as role="button", but without a key handler
      // Tab+Enter did NOTHING — the app's most frequent action was mouse-only.
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) { toggleSelected(task.id); return; }
        void open(task.id);
      }}
      onMouseEnter={() => hoverStart(task.id)}
      onMouseLeave={hoverEnd}
      aria-label={`Open task: ${task.name}`}
      className={`group relative mb-2 w-full cursor-pointer rounded-lg border bg-canvas p-2.5 text-left transition hover:bg-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${isSelected ? 'border-brand ring-1 ring-brand/60' : 'border-line hover:border-(--color-line-strong)'} ${task.priority === '1' ? 'border-l-2 border-l-warn' : ''} ${isDragging ? 'opacity-40' : ''}`}
    >
      {!done && (
        <button
          title="Mark complete"
          aria-label={`Mark "${task.name}" complete`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); void completeWithUndo(task.id, task.name, task.state); }}
          // opacity (not `hidden`): display:none can't take keyboard focus and
          // :hover never fires from a keyboard, so this was mouse-only too.
          className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-line bg-canvas text-[11px] text-muted opacity-0 transition-opacity hover:border-brand hover:text-brand focus-visible:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100"
        >
          <Icon i={Check} size={14} />
        </button>
      )}
      <CardContent task={task} dateField={dateField} hideDates={hideDates} />
    </div>
  );
}

/** Manual-order card: draggable AND a drop target, so cards can be arranged
 *  within the column (Sort = Manual only). */
function SortableCard({ task, dateField, hideDates, orderedIds, onContext }: {
  task: TaskListItem;
  dateField: DateField;
  hideDates: boolean;
  orderedIds: number[];
  onContext: (task: TaskListItem, x: number, y: number) => void;
}) {
  const open = useTaskDetailStore((s) => s.open);
  const isSelected = useTasksStore((s) => s.selectedIds.includes(task.id));
  const toggleSelected = useTasksStore((s) => s.toggleSelected);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id, data: { task } });
  const done = CLOSED.has(task.state);

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      onClick={(e) => {
        // Same routing as DraggableCard: shift = range, ctrl/cmd = toggle, plain = open.
        if (e.shiftKey) {
          const st = useTasksStore.getState();
          if (st.lastSelectedId !== null) st.selectRange(st.lastSelectedId, task.id, orderedIds);
          else st.toggleSelected(task.id);
          return;
        }
        if (e.ctrlKey || e.metaKey) { toggleSelected(task.id); return; }
        void open(task.id);
      }}
      onContextMenu={(e) => { e.preventDefault(); onContext(task, e.clientX, e.clientY); }}
      onMouseEnter={() => hoverStart(task.id)}
      onMouseLeave={hoverEnd}
      className={`group relative mb-2 w-full cursor-grab rounded-lg border bg-canvas p-2.5 text-left transition hover:bg-hover ${isSelected ? 'border-brand ring-1 ring-brand/60' : 'border-line hover:border-(--color-line-strong)'} ${task.priority === '1' ? 'border-l-2 border-l-warn' : ''} ${isDragging ? 'opacity-40' : ''}`}
    >
      {!done && (
        <button
          title="Mark complete"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); void completeWithUndo(task.id, task.name, task.state); }}
          className="absolute right-1.5 top-1.5 hidden h-5 w-5 items-center justify-center rounded-full border border-line bg-canvas text-[11px] text-muted hover:border-brand hover:text-brand group-hover:flex pointer-coarse:flex"
        >
          <Icon i={Check} size={14} />
        </button>
      )}
      <CardContent task={task} dateField={dateField} hideDates={hideDates} />
    </div>
  );
}

/** One folded stage as a compact horizontal chip inside the rail. Still a drop
 *  target: dragging a card onto the chip files it into that stage unexpanded. */
function FoldedChip({ groupKey, label, count, groupBy, onExpand }: { groupKey: string; label: string; count: number; groupBy: GroupBy; onExpand: () => void }) {
  const value = groupKey.split(':')[1] ?? '';
  const { setNodeRef, isOver } = useDroppable({ id: groupKey, data: { kind: groupBy, value }, disabled: groupBy === 'due' });
  return (
    <button
      ref={setNodeRef}
      onClick={onExpand}
      title={`Expand ${label}`}
      className={`flex h-8 w-full items-center justify-between gap-2 rounded-md border px-2.5 text-xs transition ${isOver ? 'border-brand/50 bg-brand/10 text-ink' : 'border-line/60 bg-canvas/40 text-muted hover:bg-hover hover:text-ink'}`}
    >
      <span className="truncate">{label}</span>
      <span className={`shrink-0 tabular-nums ${count > 0 ? 'text-ink' : 'text-faint'}`}>{count}</span>
    </button>
  );
}

/** ONE quiet stack for everything inactive. The previous design gave every folded
 *  stage its own full-height vertical rail — five empty stages read as a picket
 *  fence of broken planks dominating a quiet board (Allyson, 7/27: "still feels
 *  and looks off"). Best-in-class boards left-pack the WORK and compress the
 *  furniture: folded stages become one column of compact chips, and the stage
 *  management rows live at its foot instead of claiming a column of their own. */
function FoldedRail({ cols, groupBy, onExpand, manage }: {
  cols: Array<{ key: string; label: string; count: number }>;
  groupBy: GroupBy;
  onExpand: (key: string) => void;
  manage: ReactNode;
}) {
  return (
    <div className="flex h-full w-48 shrink-0 snap-start flex-col gap-1.5 pt-0.5">
      {cols.length > 0 && (
        <div className="px-0.5 text-2xs font-medium uppercase tracking-[0.04em] text-faint">
          {groupBy === 'stage' ? 'More stages' : 'More columns'}
        </div>
      )}
      {cols.map((c) => (
        <FoldedChip key={c.key} groupKey={c.key} label={c.label} count={c.count} groupBy={groupBy} onExpand={() => onExpand(c.key)} />
      ))}
      {manage && <div className={cols.length > 0 ? 'mt-2 border-t border-line/60 pt-2' : ''}>{manage}</div>}
    </div>
  );
}

function AddTaskComposer({ areaId, defaults, composerSignal = 0 }: { areaId: number; defaults: GroupCreateDefaults; composerSignal?: number }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  // Keymap 'c' → uiStore.composerRequest, routed here by BoardView (first
  // visible stage column only). Seed the ref with the mount-time value so a
  // press consumed before this column (re)mounted can't replay into a fresh
  // composer — only a stamp that CHANGES after mount opens it.
  const seenSignal = useRef(composerSignal);
  useEffect(() => {
    // 0 = "not the target" — a pin moving to another column resets this prop to
    // 0, and that transition must not open anything.
    if (!composerSignal || composerSignal === seenSignal.current) return;
    seenSignal.current = composerSignal;
    setOpen(true);
    inputRef.current?.focus(); // already-open case; a first open focuses via the effect above
  }, [composerSignal]);

  const submit = async () => {
    const n = name.trim();
    if (!n || busy) return;
    setBusy(true);
    // The column's own values ride along (its person, its star, its date) so
    // the new card lands in the column it was typed in, not somewhere else.
    const task = await createTask({ areaId, name: n, ...defaults });
    setBusy(false);
    if (task) { setName(''); inputRef.current?.focus(); } // keep open for rapid add
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-1 flex w-full items-center gap-1 rounded-md px-2 py-1.5 text-left text-[12px] text-faint transition hover:bg-hover hover:text-ink"
      >
        <Icon i={Plus} size={14} /> Add task
      </button>
    );
  }
  return (
    <div className="mt-1 rounded-md border border-line bg-canvas p-1.5">
      <textarea
        ref={inputRef}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void submit(); }
          else if (e.key === 'Escape') { setOpen(false); setName(''); }
        }}
        onBlur={() => { if (!name.trim() && !busy) setOpen(false); }}
        rows={2}
        placeholder="Task name…  (Enter to add, Esc to close)"
        className="w-full resize-none bg-transparent text-[13px] text-ink placeholder:text-faint focus:outline-none"
      />
      <div className="mt-1 flex items-center gap-2">
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => void submit()}
          disabled={busy || !name.trim()}
          className="rounded bg-brand px-2 py-1 text-[12px] font-medium text-page disabled:opacity-40"
        >
          {busy ? 'Adding…' : 'Add'}
        </button>
        <button onClick={() => { setOpen(false); setName(''); }} className="text-[12px] text-muted hover:text-ink">Cancel</button>
      </div>
    </div>
  );
}

/** Stage management for anyone on the board: grow the pipeline right on it. `bare`
 *  renders just the two rows (no column wrapper) so it can live INSIDE the
 *  folded-stages rail instead of claiming a column of its own. */
function AddStageColumn({ areaId, onEditStages, bare = false }: { areaId: number; onEditStages: () => void; bare?: boolean }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const reloadStages = useAreaStore((s) => s.reloadStages);
  const toast = useToastStore((s) => s.show);

  const submit = async () => {
    const n = name.trim();
    if (!n || busy) return;
    setBusy(true);
    try {
      await api.post(`/areas/${areaId}/stages`, { name: n });
      await reloadStages(areaId);
      setName('');
      setOpen(false);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not add the stage.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={bare ? 'flex flex-col gap-1.5' : 'flex h-full w-48 shrink-0 snap-start flex-col gap-1.5 pt-0.5'}>
      {open ? (
        <div className="rounded-md border border-line bg-canvas p-1.5">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
              else if (e.key === 'Escape') { setOpen(false); setName(''); }
            }}
            placeholder="Stage name…"
            className="w-full bg-transparent text-[13px] text-ink outline-none placeholder:text-faint"
          />
          <div className="mt-1 flex items-center gap-2">
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => void submit()} disabled={busy || !name.trim()} className="rounded bg-brand px-2 py-0.5 text-[12px] font-medium text-page disabled:opacity-40">
              {busy ? 'Adding…' : 'Add'}
            </button>
            <button onClick={() => { setOpen(false); setName(''); }} className="text-[12px] text-muted hover:text-ink">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} className="flex items-center gap-1 rounded-md border border-dashed border-line px-2 py-1.5 text-left text-[12px] text-faint transition hover:bg-hover hover:text-ink">
          <Icon i={Plus} size={14} /> Add stage
        </button>
      )}
      <button onClick={onEditStages} className="flex items-center gap-1 rounded-md border border-line px-2 py-1.5 text-left text-[12px] text-muted transition hover:bg-hover hover:text-ink" title="Add, rename, reorder, or delete columns">
        <Icon i={Settings} size={14} /> Edit stages
      </button>
    </div>
  );
}

function Column({
  areaId,
  groupKey,
  title,
  count,
  tasks,
  dateField,
  hideDates,
  groupBy,
  manualSort,
  onCollapse,
  onContext,
  onFocus,
  composerSignal,
}: {
  areaId: number;
  groupKey: string;
  title: string;
  count: number;
  tasks: TaskListItem[];
  dateField: DateField;
  hideDates: boolean;
  groupBy: GroupBy;
  manualSort: boolean;
  onCollapse: () => void;
  onContext: (task: TaskListItem, x: number, y: number) => void;
  /** Present on stage columns: clicking the title focuses this stage. */
  onFocus?: () => void;
  composerSignal?: number;
}) {
  const parentRef = useRef<HTMLDivElement | null>(null);
  const value = groupKey.split(':')[1] ?? '';
  // Every column that can honestly hold a new card offers "Add task" — not just
  // stage columns. Grouped by person or priority there used to be no way to add.
  const stages = useAreaStore((s) => s.stagesByArea[areaId]) as Stage[] | undefined;
  const addDefaults = useMemo(
    () => createDefaultsFor(groupKey, { stages: stages ?? [], dateField }),
    [groupKey, stages, dateField],
  );
  const { setNodeRef, isOver } = useDroppable({ id: groupKey, data: { kind: groupBy, value }, disabled: groupBy === 'due' });

  // THE OPINION ("it still feels overwhelming" — Allyson, 7/27): a board of
  // everything answers the wrong question. Cards nobody has TOUCHED in ~3 months
  // tuck behind a per-column "dormant" row — unless they're genuinely due soon,
  // which keeps them alive regardless. Nothing is hidden (one click), but the
  // column defaults to its LIVING work: Event Concept's 17 becomes the fresh
  // handful + "12 dormant". Same pattern that fixed the 400-task My Tasks wall.
  const [showDormant, setShowDormant] = useState(false);
  const { alive, dormant } = useMemo(() => {
    const cutoff = Date.now() - 90 * 86_400_000;
    const soon = Date.now() + 30 * 86_400_000;
    const a: TaskListItem[] = []; const d: TaskListItem[] = [];
    for (const t of tasks) {
      const touchedRaw = t.touchedAt ?? '';
      const touched = touchedRaw ? new Date(touchedRaw.includes('T') ? touchedRaw : `${touchedRaw.replace(' ', 'T')}Z`).getTime() : Number.POSITIVE_INFINITY;
      let isAlive = !(Number.isFinite(touched) && touched < cutoff);
      if (!isAlive) {
        const due = t.deadline ?? t.filmDate;
        if (due) {
          const dueMs = new Date(`${due.slice(0, 10)}T00:00:00`).getTime();
          // Due between today and +30d = alive no matter how stale the record;
          // overdue-and-untouched is the definition of dormant, so it tucks.
          if (dueMs >= Date.now() - 86_400_000 && dueMs <= soon) isAlive = true;
        }
      }
      (isAlive ? a : d).push(t);
    }
    return { alive: a, dormant: d };
  }, [tasks]);
  const visible = showDormant ? tasks : alive;

  const virtualize = visible.length > VIRTUALIZE_THRESHOLD;
  const sortable = manualSort && !virtualize; // huge columns keep plain drag
  // THIS column's visible order — the range a shift-click selects over.
  const orderedIds = useMemo(() => visible.map((t) => t.id), [visible]);
  const virtualizer = useVirtualizer({
    count: virtualize ? visible.length : 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 96,
    overscan: 8,
  });

  return (
    <div className="flex h-full w-[86vw] shrink-0 snap-center flex-col sm:w-72 sm:snap-align-none">
      <div className="mb-2 flex items-center gap-2 px-1">
        {onFocus ? (
          <button onClick={onFocus} title={`Show only ${title}, full width`} className="min-w-0 truncate rounded px-0.5 text-left text-[13px] font-medium text-ink transition hover:text-brand">
            {title}
          </button>
        ) : (
          <span className="truncate text-[13px] font-medium text-ink">{title}</span>
        )}
        <span className="rounded bg-hover px-1.5 text-[11px] text-muted">{count}</span>
        <button onClick={onCollapse} title="Collapse" className="ml-auto rounded px-1 text-faint hover:bg-hover hover:text-ink">◂</button>
      </div>
      <div
        ref={(el) => { parentRef.current = el; setNodeRef(el); }}
        className={`min-h-0 flex-1 overflow-y-auto rounded-md pr-1 transition ${isOver ? 'bg-brand/5 ring-1 ring-brand/40' : ''}`}
      >
        {tasks.length === 0 && <div className="px-1 py-2 text-xs text-faint">{isOver ? 'Drop here' : 'No tasks'}</div>}
        {virtualize ? (
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
            {virtualizer.getVirtualItems().map((vi) => (
              <div
                key={visible[vi.index].id}
                ref={virtualizer.measureElement}
                data-index={vi.index}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${vi.start}px)` }}
              >
                <DraggableCard task={visible[vi.index]} dateField={dateField} hideDates={hideDates} orderedIds={orderedIds} onContext={onContext} />
              </div>
            ))}
          </div>
        ) : sortable ? (
          <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
            {visible.map((t) => <SortableCard key={t.id} task={t} dateField={dateField} hideDates={hideDates} orderedIds={orderedIds} onContext={onContext} />)}
          </SortableContext>
        ) : (
          visible.map((t) => <DraggableCard key={t.id} task={t} dateField={dateField} hideDates={hideDates} orderedIds={orderedIds} onContext={onContext} />)
        )}
        {dormant.length > 0 && (
          <button
            onClick={() => setShowDormant((v) => !v)}
            className="mb-1 flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-faint transition hover:bg-hover hover:text-ink"
            title={showDormant ? 'Tuck untouched cards away again' : 'Cards nobody has touched in ~3 months (and not due soon)'}
          >
            <span>{showDormant ? '▾' : '▸'}</span>
            <span>{dormant.length} dormant</span>
            <span className="text-2xs">{showDormant ? 'hide' : 'show'}</span>
          </button>
        )}
      </div>
      {addDefaults && (
        <AddTaskComposer areaId={areaId} defaults={addDefaults} composerSignal={composerSignal} />
      )}
    </div>
  );
}

export function BoardView({ areaId }: { areaId: number }) {
  const area = useAreaStore((s) => s.areas.find((a) => a.id === areaId));
  const stages = useAreaStore((s) => s.stagesByArea[areaId]) as Stage[] | undefined;
  const loadStages = useAreaStore((s) => s.loadStages);
  const tasks = useTasksStore((s) => s.tasksByArea[areaId]);
  const loading = useTasksStore((s) => s.loadingArea[areaId]);
  const error = useTasksStore((s) => s.errorByArea[areaId]);
  const loadTasks = useTasksStore((s) => s.loadTasks);
  const lookupAssignees = useLookupStore((s) => s.assignees);
  const loadLookups = useLookupStore((s) => s.load);

  const { search, assigneeId, tagId, stageId, setStage, onlyUnassigned, dueThisWeek, showCompleted, groupBy, sortBy } = useFilterStore();
  const dateField: DateField = area?.calendarDateField ?? 'date_deadline';
  // Content Calendar and any future board like it: the work is already shot and
  // posted, so its cards carry no shoot/delivery date. Read off the AREA, never
  // the project name, and never the task — a card shows or hides its dates by
  // WHERE IT SITS, so the same card reads differently on Short Form and here.
  const hideDates = Boolean(area?.hideKeyDates);

  const storeKey = `cgp:board:${areaId}:collapse`;
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  useEffect(() => {
    try { const raw = localStorage.getItem(storeKey); setOverrides(raw ? JSON.parse(raw) : {}); }
    catch { setOverrides({}); }
  }, [storeKey]);
  const [activeTask, setActiveTask] = useState<TaskListItem | null>(null);
  const [stageEditor, setStageEditor] = useState(false);
  // Right-click acts (§5D): one menu instance for the whole board, fed by the
  // card that was clicked. Cleared on project switch — a menu naming a task
  // from the previous board must not survive into the next one.
  const [ctxMenu, setCtxMenu] = useState<{ task: TaskListItem; x: number; y: number } | null>(null);
  // "A moved card can carry a note for the next person" (NOW tier): after a
  // stage drag, a small optional line offers to say something to whoever picks
  // it up next. Type it or let it fade — never a pop-up, never required.
  const [moveNote, setMoveNote] = useState<{ taskId: number; taskName: string; stageName: string; text: string; busy: boolean } | null>(null);
  useEffect(() => {
    if (!moveNote || moveNote.text || moveNote.busy) return; // typing/busy = keep it
    const t = setTimeout(() => setMoveNote((m) => (m && !m.text && !m.busy ? null : m)), 10_000);
    return () => clearTimeout(t);
  }, [moveNote]);
  useEffect(() => { setCtxMenu(null); setMoveNote(null); }, [areaId]);
  const user = useAuthStore((s) => s.user);
  const canManage = !!user && (user.isAdmin || area?.managerId === user.uid);
  // Anyone on the board may shape its stages (v1.96.0); the step-owner picker stays with canManage.
  const canEditStages = canManage || !!area?.isMember;
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
  );

  useEffect(() => { void loadStages(areaId); }, [areaId, loadStages]);
  useEffect(() => { void loadTasks(areaId, showCompleted); }, [areaId, showCompleted, loadTasks]);
  useEffect(() => { void loadLookups(); }, [loadLookups]);

  const columns = useMemo(() => {
    // Grouped by stage, the stage filter renders as the FOCUSED view instead
    // (passing it here would draw every other column empty). Any other
    // grouping has no focused view — the filter must bite right here.
    const visible = filterTasks(tasks ?? [], { search, assigneeId, tagId, stageId: groupBy === 'stage' ? null : stageId, onlyUnassigned, dueThisWeek, dateField });
    return groupTasks(visible, groupBy, { stages: stages ?? [], dateField }).map((g) => ({
      ...g,
      tasks: sortTasks(g.tasks, sortBy, dateField),
    }));
  }, [tasks, stages, groupBy, sortBy, search, assigneeId, tagId, stageId, onlyUnassigned, dueThisWeek, dateField]);

  // Which columns start collapsed: folded stages, empty columns, and terminal/reference stages
  // (Archive, Completed, Old…) — so active work dominates. Users' manual toggles win and persist.
  const defaultCollapsed = (c: { fold: boolean; count: number; label: string }) =>
    c.fold || c.count === 0 || TERMINAL_STAGE.test(c.label);
  const collapsed = useMemo(() => {
    const s = new Set<string>();
    for (const c of columns) {
      const eff = c.key in overrides ? overrides[c.key] : defaultCollapsed(c);
      if (eff) s.add(c.key);
    }
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, overrides]);

  const toggle = (key: string) => {
    const col = columns.find((c) => c.key === key);
    const curr = col ? (key in overrides ? overrides[key] : defaultCollapsed(col)) : false;
    const next = { ...overrides, [key]: !curr };
    setOverrides(next);
    try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch { /* ignore */ }
  };

  // Keymap 'c' (Layout) stamps uiStore.composerRequest; each fresh stamp is
  // pinned to the first visible stage column AT PRESS TIME, whose composer opens
  // from it. Pinning (not recomputing per render) keeps a later collapse/expand
  // from replaying the press into a different column; the mount-time ref keeps a
  // stamp consumed on another board from firing here.
  const composerRequest = useUiStore((s) => s.composerRequest);
  const consumedComposerRef = useRef(composerRequest);
  const [composerTarget, setComposerTarget] = useState<{ key: string; at: number } | null>(null);
  useEffect(() => {
    if (composerRequest === consumedComposerRef.current) return;
    consumedComposerRef.current = composerRequest;
    const first = columns.find((c) => !collapsed.has(c.key) && c.key.startsWith('stage:') && Number.isInteger(Number(c.key.split(':')[1] ?? '')));
    if (first) setComposerTarget({ key: first.key, at: composerRequest });
  }, [composerRequest, columns, collapsed]);

  const onDragStart = (e: DragStartEvent) => setActiveTask((e.active.data.current?.task as TaskListItem) ?? null);
  const toast = useToastStore((s) => s.show);

  /** Manual mode: dropped on another card (or column end) → persist the new
   *  column order. Ordering runs over the FULL stage list, not the filtered
   *  view — otherwise a reorder-while-filtered scrambles hidden tasks. */
  const reorderWithin = (task: TaskListItem, overTask: TaskListItem | null) => {
    const all = (useTasksStore.getState().tasksByArea[areaId] ?? [])
      .filter((t) => t.stageId === task.stageId)
      .sort((a, b) => a.sequence - b.sequence || a.id - b.id);
    // The server reorder endpoint caps at 100 ids; the whole stage is rewritten,
    // so a stage over 100 (possible when a filter hides the size) can't be
    // hand-ordered. Say so instead of failing with a raw 400.
    if (all.length > 100) {
      toast(`This stage has ${all.length} cards — hand-ordering is only available up to 100.`, 'info');
      return;
    }
    const ids = all.map((t) => t.id);
    const from = ids.indexOf(task.id);
    const to = overTask ? ids.indexOf(overTask.id) : ids.length - 1; // null = move to end
    if (from < 0 || to < 0 || from === to) return;
    const newOrder = arrayMove(ids, from, to);
    // Optimistic: rewrite local sequences to match the new order instantly.
    const seqById = new Map(newOrder.map((id, i) => [id, (i + 1) * 10]));
    useTasksStore.setState((s) => {
      const list = s.tasksByArea[areaId];
      if (!list) return s;
      return { tasksByArea: { ...s.tasksByArea, [areaId]: list.map((t) => (seqById.has(t.id) ? { ...t, sequence: seqById.get(t.id)! } : t)) } };
    });
    api.post(`/areas/${areaId}/tasks/reorder`, { stageId: task.stageId, orderedIds: newOrder })
      .catch((err) => {
        toast(err instanceof Error ? err.message : 'Could not save the new order.', 'error');
        const st = useTasksStore.getState();
        void st.loadTasks(areaId, st.loadedClosed[areaId] ?? false, true);
      });
  };

  // Plain text → one safe HTML paragraph (the messages route sanitizes again;
  // escaping here keeps a typed '<' visible instead of vanishing).
  const escapeMoveNote = (t: string) =>
    `<p>${t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`;

  const offerMoveNote = (task: TaskListItem, stageId: number) => {
    const stageName = (stages ?? []).find((st) => st.id === stageId)?.name ?? 'the next step';
    setMoveNote({ taskId: task.id, taskName: task.name, stageName, text: '', busy: false });
  };

  const onDragEnd = (e: DragEndEvent) => {
    setActiveTask(null);
    const task = e.active.data.current?.task as TaskListItem | undefined;
    if (!task) return;
    // Dropped on another CARD (manual sort): same column → reorder;
    // different column → move to that card's stage.
    const overTask = e.over?.data.current?.task as TaskListItem | undefined;
    if (overTask && overTask.id !== task.id && groupBy === 'stage' && sortBy === 'manual') {
      if (overTask.stageId === task.stageId) reorderWithin(task, overTask);
      else { void moveStage(task.id, overTask.stageId).then((ok) => { if (ok) offerMoveNote(task, overTask.stageId); }); }
      return;
    }
    const data = e.over?.data.current as { kind: GroupBy; value: string } | undefined;
    if (!data) return;
    if (data.kind === 'stage') {
      const sid = Number(data.value);
      if (Number.isInteger(sid) && task.stageId !== sid) { void moveStage(task.id, sid).then((ok) => { if (ok) offerMoveNote(task, sid); }); }
      else if (task.stageId === sid) {
        if (sortBy !== 'manual') {
          toast('Tip: set Sort to "Manual" to arrange cards by hand.', 'info');
        } else {
          const col = columns.find((c) => c.key === `stage:${sid}`);
          if (col && col.tasks.length > VIRTUALIZE_THRESHOLD) {
            toast(`This column has ${col.tasks.length} cards — manual reorder is off for columns over ${VIRTUALIZE_THRESHOLD}.`, 'info');
          } else {
            reorderWithin(task, null); // dropped in the empty space → move to end
          }
        }
      }
    } else if (data.kind === 'assignee') {
      // A task with several assignees is rendered in EVERY one of their lanes, so
      // a drop can't tell which lane it came from. Replacing the list would wipe
      // the co-assignees — so on a shared task we ADD the target instead, and say
      // so. A single-assignee task still moves cleanly.
      const target = data.value === 'none' ? null : Number(data.value);
      const current = task.assignees.map((a) => a.id);
      const shared = current.length > 1;
      let ids: number[];
      if (target === null) ids = shared ? current : [];
      else if (current.includes(target)) ids = current;
      else ids = shared ? [...current, target] : [target];
      const same = ids.length === current.length && ids.every((id) => current.includes(id));
      if (!same) {
        const resolved = ids.map((id) => lookupAssignees.find((a) => a.id === id) ?? task.assignees.find((a) => a.id === id))
          .filter((a): a is NonNullable<typeof a> => Boolean(a));
        void patchTask(task.id, { assigneeIds: ids }, { assignees: resolved });
        if (shared && target !== null) toast('Added to that person — the other assignees were kept. Open the task to remove someone.', 'info');
      } else if (shared && target === null) {
        toast('This task has several assignees — remove them in the task panel.', 'info');
      }
    } else if (data.kind === 'priority') {
      const p = data.value === '1' ? '1' : '0';
      if (task.priority !== p) void patchTask(task.id, { priority: p }, { priority: p });
    }
  };

  if (loading && !tasks) {
    // Boot ghost — the board's real geometry (px-4 py-3 gutter, w-72 columns,
    // header row + ~88px cards, mb-2 rhythm) so the swap to live columns
    // doesn't jump. Refreshes with tasks already on screen never come here.
    return (
      <div aria-hidden className="flex h-full gap-3 overflow-hidden px-4 py-3">
        {[0, 1, 2].map((col) => (
          <div key={col} className="flex h-full w-[86vw] shrink-0 flex-col sm:w-72">
            <div className="mb-2 flex items-center gap-2 px-1">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-4 w-7" />
            </div>
            {[0, 1, 2, 3].map((card) => (
              <Skeleton key={card} className="mb-2 h-[88px] w-full rounded-lg" />
            ))}
          </div>
        ))}
      </div>
    );
  }
  if (error) return <LoadError message={error} onRetry={() => void loadTasks(areaId, showCompleted, true)} />;

  const totalVisible = columns.reduce((n, c) => n + c.tasks.length, 0);

  // Focused stage ("If I want to just click on published how do I just see
  // those?" — Allyson, 8/10): one column, full width, cards in a grid. Only
  // meaningful while the board is grouped by stage; a stageId left over from
  // another project (stages are per-area) simply doesn't resolve and renders
  // the normal board — the FilterBar chip still shows it, and Clear resets.
  const focusedCol = groupBy === 'stage' && stageId != null
    ? columns.find((c) => c.key === `stage:${stageId}`) ?? null
    : null;

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      {focusedCol ? (
        /* One stage, the whole screen. Cards flow in a grid instead of a
           72-wide strip — 109 "Published" cards become rows of them. Drag
           lives in the normal board (there is nowhere to drop here). */
        <div className="flex h-full min-h-0 flex-col px-4 py-3">
          <div className="mb-3 flex shrink-0 items-center gap-2">
            <span className="text-[15px] font-semibold text-ink">{focusedCol.label}</span>
            <span className="rounded bg-hover px-1.5 text-[11px] text-muted">{focusedCol.tasks.length}</span>
            <button
              onClick={() => setStage(null)}
              className="ml-2 rounded-md border border-line px-2 py-1 text-xs text-muted transition hover:bg-hover hover:text-ink"
            >
              ✕ Show all columns
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {focusedCol.tasks.length === 0 && (
              <div className="pt-10 text-center text-sm text-faint">Nothing here matches your filters.</div>
            )}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] items-start gap-2 pb-4">
              {(() => { const ids = focusedCol.tasks.map((x) => x.id); return focusedCol.tasks.map((t) => (
                <DraggableCard key={t.id} task={t} dateField={dateField} hideDates={hideDates} orderedIds={ids} onContext={(tk, x, y) => setCtxMenu({ task: tk, x, y })} />
              )); })()}
            </div>
          </div>
        </div>
      ) : (
      <>
      {/* snap-proximity, not -mandatory: with mandatory the scroller cannot come to
          rest on the trailing Add/Edit-stages column, making it unreachable on a phone. */}
      <div className="relative flex h-full snap-x snap-proximity gap-3 overflow-x-auto px-4 py-3 sm:snap-none">
        {/* WORK first, furniture last: expanded columns keep their pipeline order;
            everything folded compresses into ONE rail at the end instead of a
            picket fence of vertical slivers interleaved through the board. */}
        {columns.filter((c) => !collapsed.has(c.key)).map((c) => (
          <Column key={c.key} areaId={areaId} groupKey={c.key} title={c.label} count={c.count} tasks={c.tasks} dateField={dateField} hideDates={hideDates} groupBy={groupBy} manualSort={groupBy === 'stage' && sortBy === 'manual'} onCollapse={() => toggle(c.key)} onContext={(t, x, y) => setCtxMenu({ task: t, x, y })} onFocus={groupBy === 'stage' && c.key.startsWith('stage:') && Number.isInteger(Number(c.key.split(':')[1])) ? () => setStage(Number(c.key.split(':')[1])) : undefined} composerSignal={composerTarget?.key === c.key ? composerTarget.at : 0} />
        ))}
        {(columns.some((c) => collapsed.has(c.key)) || (groupBy === 'stage' && canEditStages)) && (
          <FoldedRail
            cols={columns.filter((c) => collapsed.has(c.key)).map((c) => ({ key: c.key, label: c.label, count: c.count }))}
            groupBy={groupBy}
            onExpand={toggle}
            manage={groupBy === 'stage' && canEditStages
              ? <AddStageColumn areaId={areaId} onEditStages={() => setStageEditor(true)} bare />
              : null}
          />
        )}
        {/* The first screen after creating a project. Say "project" (not the internal
            word "area") and point at the next step instead of dead-ending. */}
        {columns.length === 0 && (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-6 text-center">
            <div className="text-sm text-muted">This project has no columns yet.</div>
            <div className="text-xs text-faint">Use <Icon i={Settings} size={14} className="inline align-[-0.2em]" /> Edit stages to add columns like To Do · In Progress · Done, then <Icon i={Plus} size={14} className="inline align-[-0.2em]" /> Add task.</div>
          </div>
        )}
        {columns.length > 0 && totalVisible === 0 && (
          <div className="pointer-events-none absolute inset-x-0 top-1/2 text-center text-sm text-faint">
            {showCompleted ? 'No tasks match your filters.' : 'No open tasks — try “Show completed”.'}
          </div>
        )}
      </div>
      </>
      )}
      {moveNote && (
        <div className="pointer-events-auto fixed inset-x-0 bottom-4 z-40 mx-auto flex w-[min(560px,92vw)] items-center gap-2 rounded-lg border border-line bg-canvas px-3 py-2 shadow-xl animate-fade-in">
          <span className="shrink-0 text-[11px] text-faint">→ {moveNote.stageName}</span>
          <input
            autoFocus={false}
            value={moveNote.text}
            onChange={(e) => setMoveNote((m) => (m ? { ...m, text: e.target.value } : m))}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setMoveNote(null);
              if (e.key === 'Enter' && moveNote.text.trim() && !moveNote.busy) {
                e.preventDefault();
                const note = moveNote.text.trim();
                setMoveNote((m) => (m ? { ...m, busy: true } : m));
                api.post(`/tasks/${moveNote.taskId}/messages`, { body: escapeMoveNote(note) })
                  .then(() => { toast('Note added to the card.', 'info'); setMoveNote(null); })
                  .catch(() => { toast('Could not add the note — open the card and comment instead.', 'error'); setMoveNote(null); });
              }
            }}
            placeholder="Say anything for the next person?  (Enter · Esc to skip)"
            className="w-full bg-transparent text-[13px] text-ink outline-none placeholder:text-faint"
          />
          <button onClick={() => setMoveNote(null)} className="shrink-0 text-[11px] text-faint hover:text-ink">skip</button>
        </div>
      )}
      <DragOverlay dropAnimation={null}>
        {activeTask ? (
          <div className="w-72 rotate-1 rounded-lg border border-brand/50 bg-canvas p-2.5 shadow-lg">
            <CardContent task={activeTask} dateField={dateField} hideDates={hideDates} />
          </div>
        ) : null}
      </DragOverlay>
      {stageEditor && stages && (
        <StageManagerModal
          areaId={areaId}
          stages={stages}
          taskCountByStage={(tasks ?? []).reduce((m, t) => m.set(t.stageId, (m.get(t.stageId) ?? 0) + 1), new Map<number, number>())}
          canSetOwners={canManage}
          onClose={() => setStageEditor(false)}
        />
      )}
      {ctxMenu && (
        <TaskContextMenu
          task={ctxMenu.task}
          position={{ x: ctxMenu.x, y: ctxMenu.y }}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </DndContext>
  );
}
